import { EFinancingCompany } from '@if/core/types'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material'
import { reportErrorToConsole } from '@src/services/error-logger'
import axios, { AxiosRequestConfig } from 'axios'
import { ChangeEvent, useEffect, useState } from 'react'

interface DialogProps {
  onMockedLoginSelected: (username: string, companies: EFinancingCompany[]) => void
  open: boolean
  onClose: () => void
}

interface MockedLogin {
  id: string
  username: string
  roles: string[]
}

interface MockedLoginItemProps {
  onTestCaseSelected: (username: string) => void
  mockedLogin: MockedLogin
  selected: boolean
}

function loadMockedLogins(): Promise<MockedLogin[]> {
  const payload: AxiosRequestConfig = {
    method: 'GET',
    baseURL: import.meta.env.VITE_BACKOFFICE_API_URL,
    url: '/MockLogin',
  }
  return axios.request(payload).then((response) => {
    const res = response.data as MockedLogin[]
    res.forEach((x: MockedLogin, index) => {
      x.id = index.toString()
    })
    return response.data as MockedLogin[]
  })
}

const TestCaseItem = ({ onTestCaseSelected, mockedLogin, selected }: MockedLoginItemProps) => {
  return (
    <>
      <ListItemButton onClick={() => onTestCaseSelected(mockedLogin.username)} selected={selected}>
        <ListItemText primary={mockedLogin.username} secondary={mockedLogin.roles.join(', ')} />
      </ListItemButton>
      <Divider />
    </>
  )
}

const ListMockedLoginDialog = ({ open, onClose, onMockedLoginSelected }: DialogProps) => {
  const [selectedUsername, setSelectedUsername] = useState<string | null>(null)
  const [mockedLogins, setTestCases] = useState<MockedLogin[]>([])
  const [isLoading, setLoading] = useState(false)
  const [selectedCompanies, setSelectedCompanies] = useState<EFinancingCompany[]>([])

  useEffect(() => {
    if (open && mockedLogins.length === 0) {
      setLoading(true)
      setSelectedCompanies([EFinancingCompany.Iceberg])
      loadMockedLogins()
        .then((values: MockedLogin[]) => {
          setLoading(false)
          setTestCases(values)
        })
        .catch(reportErrorToConsole)
    }
  }, [open, mockedLogins])

  const handleCompanyChange = (event: ChangeEvent<HTMLInputElement>, company: EFinancingCompany) => {
    if (event.target.checked) {
      setSelectedCompanies([...selectedCompanies, company])
    } else {
      setSelectedCompanies(selectedCompanies.filter((c) => c !== company))
    }
  }

  return (
    <Dialog open={open} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description" maxWidth="xl">
      <DialogTitle style={{ borderBottom: '1px solid' }} id="alert-dialog-title">
        Sélectionner un mock pour le login
      </DialogTitle>
      <DialogContent>
        <Box height={400} width={900}>
          {isLoading && <CircularProgress />}
          {!isLoading && (
            <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
              {mockedLogins.map((login) => (
                <TestCaseItem
                  key={login.id}
                  mockedLogin={login}
                  onTestCaseSelected={setSelectedUsername}
                  selected={login.username === selectedUsername}
                />
              ))}
            </List>
          )}
        </Box>
      </DialogContent>

      <Box padding={2} sx={{ p: 2, borderTop: '1px solid gray' }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Associer cette connexion avec les compagnies suivantes :
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={selectedCompanies.includes(EFinancingCompany.IFinance)}
              onChange={(event) => handleCompanyChange(event, EFinancingCompany.IFinance)}
            />
          }
          label="IFinance"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={selectedCompanies.includes(EFinancingCompany.Iceberg)}
              onChange={(event) => handleCompanyChange(event, EFinancingCompany.Iceberg)}
            />
          }
          label="Iceberg"
        />
      </Box>
      <DialogActions>
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          <Button type="button" variant="contained" color="error" onClick={onClose}>
            Annuler
          </Button>
        </Box>
        <Box sx={{ flex: '1 1 auto' }} />
        <Box sx={{ display: 'flex', flexDirection: 'row', p: 2 }}>
          <Button
            type="button"
            variant="contained"
            color="success"
            disabled={selectedUsername === null || selectedCompanies.length === 0}
            onClick={() => {
              if (selectedUsername) onMockedLoginSelected(selectedUsername, selectedCompanies)
            }}
          >
            Charger
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

export default ListMockedLoginDialog
